import { formatDuration } from './formatDuration';

const CHART_FALLBACK_COLORS = ['#E8B923', '#8AAF2E', '#F07676', '#3B9DDB'];

function formatHoursDelta(hours) {
  const value = Number(hours) || 0;
  if (value <= 0) return '0 Hrs';
  return `${Number.isInteger(value) ? value : value.toFixed(1)}+ Hrs`;
}

function getPeriodLabel(performancePeriod) {
  return performancePeriod === 'this_month' ? 'From this month' : 'From this week';
}

function getStudyHoursDelta(summary, performancePeriod) {
  if (performancePeriod === 'this_month') {
    return summary.total_study_hours_this_month ?? summary.total_study_hours_this_week;
  }
  return summary.total_study_hours_this_week;
}

function getChaptersDelta(summary, performancePeriod) {
  if (performancePeriod === 'this_month') {
    return summary.chapters_completed_this_month ?? summary.chapters_completed_this_week;
  }
  return summary.chapters_completed_this_week;
}

export function mapLearningProgressOverview(data, performancePeriod = 'this_week') {
  if (!data) {
    return { chartItems: [], stats: null, maxMinutes: 120 };
  }

  const performanceSubjects = data.performance?.subjects || [];
  const maxMinutes = Math.max(
    120,
    ...performanceSubjects.map((subject) => Number(subject.minutes_watched) || 0)
  );
  const roundedMax = Math.ceil(maxMinutes / 30) * 30;
  const summary = data.summary || {};
  const periodLabel = getPeriodLabel(performancePeriod);

  const chartItems = performanceSubjects.map((subject, index) => ({
    id: String(subject.subject_id ?? index),
    label: subject.name,
    minutes: Number(subject.minutes_watched) || 0,
    color: subject.color_code || CHART_FALLBACK_COLORS[index % CHART_FALLBACK_COLORS.length],
  }));

  const stats = {
    studyHours: Math.round((Number(summary.total_study_hours) || 0) * 10) / 10,
    studyHoursDelta1: formatHoursDelta(getStudyHoursDelta(summary, performancePeriod)),
    studyHoursDelta2: periodLabel,
    chaptersCompleted: summary.chapters_completed || 0,
    chaptersDelta1: `${getChaptersDelta(summary, performancePeriod) || 0}+`,
    chaptersDelta2: periodLabel,
  };

  return { chartItems, stats, maxMinutes: roundedMax };
}

export function mapLearningProgressSubjects(subjects = []) {
  return subjects.map((subject) => {
    const completed = subject.lessons_completed || 0;
    const total = subject.lessons_total || 0;
    const progressPercent = total > 0 ? (completed / total) * 100 : 0;
    const color = subject.color_code || '#009DA8';
    const chapters = subject.chapters || [];

    return {
      id: String(subject.subject_id),
      name: subject.name?.toUpperCase() || 'SUBJECT',
      lessonsCompleted: completed,
      lessonsTotal: total,
      continueLessonId: subject.continue_lesson_id,
      courseId: chapters[0]?.course_id ?? null,
      segments: [{ color, width: Math.max(progressPercent, completed > 0 ? 4 : 0) }],
      chapters: chapters.map((chapter) => ({
        id: chapter.id,
        name: chapter.chapter_name,
        lessons: chapter.lessons_count,
        timeWatched:
          chapter.time_watched_display ||
          formatDuration(Math.floor((chapter.time_watched_seconds || 0) / 60)),
        courseId: chapter.course_id,
      })),
    };
  });
}

export function mapLearningProgressData(data, performancePeriod = 'this_week') {
  if (!data) {
    return { overview: mapLearningProgressOverview(null, performancePeriod), subjects: [] };
  }

  return {
    overview: mapLearningProgressOverview(data, performancePeriod),
    subjects: mapLearningProgressSubjects(data.subjects || []),
  };
}
